import { useState, useMemo } from "react";

const allCities = [
  {
    name: "Boston",
    route: "/cities/boston",
    tagline: "Greater Boston expedited passport services for Back Bay, Cambridge, Brookline, and all surrounding communities",
    highlights: ["Greater Boston coverage", "Serving Cambridge, Somerville, Brookline, Quincy"],
    badge: "Most Popular",
    status: "live",
  },
  {
    name: "Worcester",
    route: "/worcester",
    tagline: "Central Massachusetts expedited passport processing for Worcester County and surrounding communities",
    highlights: ["Central MA coverage", "Serving Auburn, Shrewsbury, Leominster, Fitchburg"],
    badge: null,
    status: "live",
  },
  {
    name: "Springfield",
    route: "/springfield-ma",
    tagline: "Western Massachusetts expedited passport services for the Pioneer Valley and Connecticut River Valley",
    highlights: ["Western MA coverage", "Serving Chicopee, West Springfield, Holyoke"],
    badge: null,
    status: "live",
  },
  {
    name: "Lowell",
    route: "/lowell",
    tagline: "Northern Massachusetts expedited passport services for the Merrimack Valley",
    highlights: ["Merrimack Valley coverage", "Serving Chelmsford, Billerica, Tewksbury"],
    badge: null,
    status: "live",
  },
  {
    name: "Cambridge",
    route: "/cambridge",
    tagline: "Greater Boston expedited passport services near Harvard and MIT",
    highlights: ["Metro Boston coverage", "Serving Harvard Square, Kendall Square, Central Square"],
    badge: null,
    status: "live",
  },
  {
    name: "New Bedford",
    route: "/cities/new-bedford",
    tagline: "South Coast Massachusetts expedited passport services for the fishing and maritime community",
    highlights: ["South Coast coverage", "Serving Fall River, Dartmouth, Fairhaven"],
    badge: null,
    status: "live",
  },
  {
    name: "Brockton",
    route: "/cities/brockton",
    tagline: "Southeastern Massachusetts expedited passport processing for the City of Champions",
    highlights: ["Southeastern MA coverage", "Serving Avon, Stoughton, East Bridgewater"],
    badge: null,
    status: "live",
  },
  {
    name: "Quincy",
    route: "/cities/quincy-ma",
    tagline: "South Shore expedited passport services near the historic birthplace of two presidents",
    highlights: ["South Shore coverage", "Serving Braintree, Weymouth, Milton"],
    badge: null,
    status: "live",
  },
  {
    name: "Lynn",
    route: "/cities/lynn",
    tagline: "North Shore Massachusetts expedited passport services for the City of Firsts",
    highlights: ["North Shore coverage", "Serving Saugus, Revere, Peabody"],
    badge: null,
    status: "live",
  },
  {
    name: "Fall River",
    route: "/cities/fall-river",
    tagline: "South Coast Massachusetts expedited passport processing near the Taunton River",
    highlights: ["South Coast coverage", "Serving Somerset, Swansea, Westport"],
    badge: null,
    status: "live",
  },
  {
    name: "Newton",
    route: "/cities/newton",
    tagline: "Metro West expedited passport services for the Garden City",
    highlights: ["Metro West coverage", "Serving Waltham, Watertown, Needham"],
    badge: null,
    status: "live",
  },
  {
    name: "Somerville",
    route: "/cities/somerville",
    tagline: "Greater Boston expedited passport services in the vibrant city north of the Charles",
    highlights: ["Metro Boston coverage", "Serving Davis Square, Union Square, Assembly Row"],
    badge: null,
    status: "live",
  },
  {
    name: "Nantucket",
    route: "/cities/nantucket",
    tagline: "Nantucket Island expedited passport services with island-to-mainland courier for year-round and summer residents",
    highlights: ["Island-to-mainland courier", "Serving Sconset, Madaket, Surfside, Brant Point"],
    badge: "Island",
    status: "live",
  },
  {
    name: "Martha's Vineyard",
    route: "/cities/marthas-vineyard",
    tagline: "Martha's Vineyard expedited passport services with island-to-mainland courier for all six island towns",
    highlights: ["Island-to-mainland courier", "Serving Edgartown, Oak Bluffs, Vineyard Haven, Chilmark"],
    badge: "Island",
    status: "live",
  },
];

const LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

export default function MassachusettsCitiesDirectory() {
  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const lettersWithCities = useMemo(() => {
    const set = new Set<string>();
    allCities.forEach((c) => set.add(c.name[0].toUpperCase()));
    return set;
  }, []);

  const filteredCities = useMemo(() => {
    let result = allCities;
    if (activeLetter) {
      result = result.filter((c) => c.name[0].toUpperCase() === activeLetter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.tagline.toLowerCase().includes(q) ||
          c.highlights.some((h) => h.toLowerCase().includes(q))
      );
    }
    return result;
  }, [activeLetter, searchQuery]);

  const liveCount = allCities.filter((c) => c.status === "live").length;
  const comingCount = allCities.filter((c) => c.status === "coming").length;

  return (
    <section id="cities" className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">
            Massachusetts Cities
          </p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Choose Your Massachusetts Location
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed mb-2">
            Select your city to view local acceptance facilities, processing options, and
            expedited services tailored to your area.
          </p>
          <p className="text-gray-400 text-sm">
            <span className="text-[#1A365D] font-semibold">{liveCount}</span> cities live
            &nbsp;&middot;&nbsp;
            <span className="text-[#1A365D] font-semibold">{comingCount}</span> more coming
            soon
          </p>
        </div>

        <div className="max-w-xl mx-auto mb-6">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
              <i className="ri-search-line text-gray-400"></i>
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                if (e.target.value.trim() && activeLetter) setActiveLetter(null);
              }}
              placeholder="Search cities..."
              className="w-full pl-11 pr-10 py-3 bg-white border border-gray-200 rounded-lg text-sm text-[#1A365D] placeholder:text-gray-400 focus:outline-none focus:border-[#F6C344] focus:ring-1 focus:ring-[#F6C344] transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line"></i>
              </button>
            )}
          </div>
        </div>

        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex flex-wrap items-center justify-center gap-1.5 md:gap-2">
            <button
              onClick={() => setActiveLetter(null)}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all whitespace-nowrap ${
                activeLetter === null
                  ? "bg-[#1A365D] text-white"
                  : "bg-white text-gray-500 hover:bg-[#EEF2F8] hover:text-[#1A365D] border border-gray-200"
              }`}
            >
              All
            </button>
            {LETTERS.map((letter) => {
              const hasCities = lettersWithCities.has(letter);
              const isActive = activeLetter === letter;
              return (
                <button
                  key={letter}
                  onClick={() => hasCities && setActiveLetter(isActive ? null : letter)}
                  disabled={!hasCities}
                  className={`w-8 h-8 md:w-9 md:h-9 rounded-md text-sm font-semibold flex items-center justify-center transition-all ${
                    isActive
                      ? "bg-[#1A365D] text-white"
                      : hasCities
                        ? "bg-white text-[#1A365D] hover:bg-[#EEF2F8] border border-gray-200 cursor-pointer"
                        : "bg-gray-100 text-gray-300 border border-gray-100 cursor-not-allowed"
                  }`}
                >
                  {letter}
                </button>
              );
            })}
          </div>
        </div>

        {(activeLetter || searchQuery) && (
          <div className="flex items-center justify-between max-w-7xl mb-6">
            <p className="text-sm text-gray-500">
              Showing{" "}
              <span className="font-semibold text-[#1A365D]">{filteredCities.length}</span>{" "}
              {filteredCities.length === 1 ? "city" : "cities"}
              {activeLetter && (
                <>
                  {" "}
                  starting with <span className="font-semibold text-[#1A365D]">{activeLetter}</span>
                </>
              )}
              {searchQuery && (
                <>
                  {" "}
                  matching <span className="font-semibold text-[#1A365D]">&ldquo;{searchQuery}&rdquo;</span>
                </>
              )}
            </p>
            <button
              onClick={() => {
                setActiveLetter(null);
                setSearchQuery("");
              }}
              className="text-sm text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors whitespace-nowrap"
            >
              Clear filters
            </button>
          </div>
        )}

        {filteredCities.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCities.map((city, i) =>
              city.status === "live" ? (
                <a
                  key={i}
                  href={city.route}
                  className="group relative bg-white border border-gray-200 rounded-lg p-7 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
                >
                  {city.badge && (
                    <span className="absolute top-4 right-4 bg-[#1A365D] text-white text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap">
                      {city.badge}
                    </span>
                  )}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                      <i className="ri-map-pin-2-line text-[#1A365D] text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A365D] text-lg">{city.name}</h3>
                      <p className="text-gray-400 text-xs">Massachusetts</p>
                    </div>
                  </div>
                  <p className="text-gray-500 text-sm leading-relaxed mb-4">{city.tagline}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {city.highlights.map((h, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1.5 text-[#1A365D] font-semibold text-sm group-hover:text-[#F6C344] transition-colors mt-auto whitespace-nowrap">
                    View Services{" "}
                    <i className="ri-arrow-right-line group-hover:translate-x-0.5 transition-transform"></i>
                  </div>
                </a>
              ) : (
                <div
                  key={i}
                  className="group relative bg-white border border-dashed border-gray-300 rounded-lg p-7 flex flex-col"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-100 flex-shrink-0">
                      <i className="ri-time-line text-gray-400 text-xl"></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-400 text-lg">{city.name}</h3>
                      <span className="inline-block bg-gray-100 text-gray-500 text-xs font-semibold px-2 py-0.5 rounded-full mt-0.5">
                        Coming Soon
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4">{city.tagline}</p>
                  <ul className="space-y-2 mb-6 flex-1">
                    {city.highlights.map((h, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-gray-400">
                        <i className="ri-check-line text-gray-300 flex-shrink-0"></i>
                        {h}
                      </li>
                    ))}
                  </ul>
                  <div className="inline-flex items-center gap-1.5 text-gray-400 font-semibold text-sm mt-auto whitespace-nowrap">
                    Coming Soon <i className="ri-time-line"></i>
                  </div>
                </div>
              )
            )}
          </div>
        ) : (
          <div className="text-center py-16">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#EEF2F8] mx-auto mb-4">
              <i className="ri-search-line text-[#1A365D] text-2xl"></i>
            </div>
            <h3 className="font-bold text-[#1A365D] text-lg mb-2">No cities found</h3>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              Try a different search term or letter. We are always adding new Massachusetts locations.
            </p>
            <button
              onClick={() => {
                setActiveLetter(null);
                setSearchQuery("");
              }}
              className="mt-4 inline-flex items-center gap-1.5 text-[#1A365D] hover:text-[#F6C344] font-semibold text-sm transition-colors whitespace-nowrap"
            >
              Show all cities <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}